import { DownloadDesignsButton } from "@/components/Button/Cases/DownloadDesignsButton";
import { CaseStatusIndicator } from "@/components/CaseStatus/CaseStatus";
import { DetailsGrid, DetailsGridItem, ExtendedDetails } from "@/components/ExtendedDetails/ExtendedDetails";
import { MultipleSelectedSvg, NoneSelectedSvg } from "@/components/Icon/Icon";
import { Model } from "@/components/Model/Model";
import { Viewport, ViewportContainer, ViewportLoading } from "@/components/Viewport/Viewport";
import {
  GetCaseApiV1CasesCaseIdGetApiArg,
  useGetCaseApiV1CasesCaseIdGetQuery,
  useRetryBasingApiV1PutMutation,
  useSearchUserApiV1UsersUserIdGetQuery
} from "@/store/clientApi";
import { Box, Link, Typography, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { FC, ReactNode, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

import { RejectPillButton } from "@/components/Button/Cases/RejectPillButton";
import { StatusBanner } from "@/components/StatusBanner/StatusBanner";
import { StyledTooltip } from "@/components/StyledTooltip/StyledTooltip";
import { INCOMPLETE_CASE_STATUSES } from "@/constants/common";
import { PH_NO_CAPTURE } from "@/constants/posthog";
import { designTableConstants } from "@/constants/designTableConstants";
import { FixableEventLogErrorInfo } from "@/constants/errors";
import {
  clearSelectedIds,
  selectDetailsItemId,
  selectDetailsItemLabel,
  selectDetailsItemType,
  selectDetailsNumSelected,
  selectDrawerOpen,
  setDrawerOpen
} from "@/store/slices/detailsDrawerSlice";
import { availableViewportModel, selectModelDownloadError, selectModelLoaded, setModelError } from "@/store/slices/viewportSlice";
import { RootState, store } from "@/store/store";
import { DataObject, ScanDataObject } from "@/typed/CaseStateData";
import { formatDateTime } from "@/utils/date";
import { rotationDegreesToEuler } from "@/utils/geometryUtils";
import { ensureError, localizeError } from "@/utils/helpers";
import { getDataFromIndexedDB, getStoreOfModel } from "@/utils/indexeddb";
import toast from "react-hot-toast";

interface CaseViewportProps {
  actionBarButtons?: ReactNode;
}

const getCaseQuery = (caseId?: string): GetCaseApiV1CasesCaseIdGetApiArg => ({
  caseId: Number(caseId)
});

const CaseViewport: FC<CaseViewportProps> = ({ actionBarButtons }) => {
  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];
  const [modelData, setModelData] = useState<DataObject[] | undefined>();

  const atMostOneSelected = useSelector((state: RootState) => selectDetailsNumSelected(state, basePath, selectedTab) < 2);
  const selectedCaseId = useSelector((state: RootState) => selectDetailsItemId(state, basePath, selectedTab));
  const selectedModelLabel = useSelector((state: RootState) => selectDetailsItemLabel(state, basePath, selectedTab));
  const { selectedCaseStatus, caseName, caseContent } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(selectedCaseId), {
    selectFromResult: ({ data }) => ({
      selectedCaseStatus: data?.status,
      caseName: data?.name,
      caseContent: data?.content
    })
  });

  const detailsItemType = useSelector((state: RootState) => selectDetailsItemType(state, basePath, selectedTab));
  const caseItemType = selectedCaseStatus
    ? INCOMPLETE_CASE_STATUSES.includes(selectedCaseStatus)
      ? "scan"
      : "based-model"
    : "based-model";
  // Use the case item type when viewing combined models
  const selectedModelType = selectedModelLabel === "combined" ? caseItemType : (detailsItemType ?? "based-model");
  const storeType = getStoreOfModel(selectedModelType);

  const selectedModelHasLoaded = useSelector((state: RootState) => selectModelLoaded(state, selectedCaseId ?? "", storeType));
  const modelViewportError = useSelector((state: RootState) => selectModelDownloadError(state, selectedCaseId ?? "", storeType));
  const isSelected = selectedCaseId !== undefined;

  const loadModelData = useMemo(
    () => async (abortSignal: AbortSignal) => {
      if (selectedCaseId === undefined || abortSignal.aborted) {
        return;
      }

      setModelData(undefined);

      const modelLabel = selectedModelLabel ?? "combined";
      const id = parseInt(selectedCaseId);

      let data: (DataObject | ScanDataObject)[] | undefined = undefined;

      const modelAvailable = await store.dispatch(
        availableViewportModel({
          caseId: id,
          caseStatus: selectedCaseStatus,
          content: caseContent ?? "combined",
          store: storeType
        })
      );

      // Check signal wasn't aborted during await
      if (abortSignal.aborted) {
        return;
      }

      if (modelAvailable.payload) {
        const modelCachedData = await getDataFromIndexedDB(selectedCaseId, storeType);

        // Check signal wasn't aborted during await
        if (abortSignal.aborted) {
          return;
        }

        if (modelCachedData === null) {
          store.dispatch(setModelError({ error: "Unable to get cached model data", id: selectedCaseId, store: storeType }));
          return;
        }

        data = modelCachedData?.data;
      }

      if (data !== undefined && data.length > 0) {
        let selectedModelData: DataObject[] = [];
        if (modelLabel === "combined") {
          // Return both "upper" and "lower" models
          selectedModelData = data.filter((item) => item.label === "upper" || item.label === "lower");
        } else {
          // Return the single model corresponding to "upper" or "lower"
          selectedModelData = data.filter((item) => item.label === selectedModelLabel);
        }

        if (selectedModelData.length > 0) {
          setModelData(selectedModelData);
        }
      }
    },
    [selectedCaseId, selectedCaseStatus, selectedModelLabel, caseContent, storeType]
  );

  useEffect(() => {
    if (modelData === undefined && selectedModelHasLoaded) {
      const controller = new AbortController();
      loadModelData(controller.signal);
      return () => controller.abort();
    }
  }, [selectedModelHasLoaded, loadModelData, modelData]);

  useEffect(() => {
    const controller = new AbortController();
    loadModelData(controller.signal);
    return () => controller.abort();
  }, [selectedCaseId, selectedModelLabel, storeType, loadModelData]);

  if (selectedCaseId && atMostOneSelected) {
    if (caseName !== undefined && modelData) {
      return (
        <Sentry.ErrorBoundary
          beforeCapture={(scope) => {
            scope.setTag("location", "case inspector model viewer");
          }}
          fallback={
            <ViewportContainer sx={{ display: "flex", height: "100%" }}>
              <NoneSelectedSvg />
            </ViewportContainer>
          }
        >
          <Viewport
            title={<Title />}
            maximisedFooter={
              selectedCaseId && (
                <Box
                  component="div"
                  sx={{
                    display: "flex",
                    width: "100%",
                    justifyContent: "flex-end",
                    gap: "6px"
                  }}
                >
                  {actionBarButtons}
                </Box>
              )
            }
          >
            <Model fileData={modelData} rotation={selectedModelType === "based-model" ? rotationDegreesToEuler(-90, 0, 0) : undefined} />
          </Viewport>
        </Sentry.ErrorBoundary>
      );
    } else if (!modelViewportError) {
      return <ViewportLoading modelId={selectedCaseId ?? ""} modelStore={storeType} />;
    } else {
      return (
        <ViewportContainer sx={{ height: "100%" }}>
          <NoneSelectedSvg />
        </ViewportContainer>
      );
    }
  } else if (!isSelected && atMostOneSelected) {
    return (
      <ViewportContainer sx={{ height: "100%" }}>
        <NoneSelectedSvg />
      </ViewportContainer>
    );
  } else {
    return (
      <ViewportContainer sx={{ height: "100%" }}>
        <MultipleSelectedSvg />
      </ViewportContainer>
    );
  }
};

const RetryBanner = ({ id }: { id: string }) => {
  const { t } = useTranslation("cases");
  const theme = useTheme();

  const [retryBasing] = useRetryBasingApiV1PutMutation();
  const { status, eventLogs, caseOptions } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(id), {
    selectFromResult: ({ data }) => ({
      status: data?.status,
      eventLogs: data?.logs,
      caseOptions: data?.case_options
    })
  });

  if (status === undefined || status !== "failed" || eventLogs === undefined || caseOptions === undefined) {
    return <></>;
  }

  const retryInfo = eventLogs
    .map((log) => ({ status: log.status, fix: FixableEventLogErrorInfo[log.algorithm_step] }))
    .find(({ status, fix }) => status === "failed" && fix !== undefined);
  if (retryInfo === undefined || retryInfo.fix === undefined) {
    return <></>;
  }
  const fix = retryInfo.fix;

  const handleRetry = async () => {
    try {
      await retryBasing({ caseId: Number(id), retryBasingRequest: { case_options: fix.options(caseOptions) } }).unwrap();
    } catch (err) {
      toast.error(localizeError(t, ensureError(err)));
    }
  };

  return (
    <StatusBanner
      sx={{ paddingX: "16px" }}
      variant="warning"
      message={
        <Trans
          t={t}
          i18nKey={fix.translationKey}
          components={{
            a: <Link color={theme.surface.onVariant} component={"button"} onClick={() => handleRetry()} />
          }}
        />
      }
      open={true}
      closable={false}
      borderTop
      disableIcon
    />
  );
};

const Title = () => {
  const { t } = useTranslation("cases");
  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];
  const numSelected = useSelector((state: RootState) => selectDetailsNumSelected(state, basePath, selectedTab));
  const selectedCaseId = useSelector((state: RootState) => selectDetailsItemId(state, basePath, selectedTab));
  // Retrieve label and type for subrow selection
  const selectedItemLabel = useSelector((state: RootState) => selectDetailsItemLabel(state, basePath, selectedTab));
  const selectedItemType = useSelector((state: RootState) => selectDetailsItemType(state, basePath, selectedTab));
  const { caseName } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(selectedCaseId), {
    selectFromResult: ({ data }) => ({
      caseName: data?.name
    })
  });

  // Base title: either the case name or number of selected items.
  let title = caseName
    ? caseName
    : t("common:label.itemSelected", {
        count: numSelected
      });

  // If a subrow is selected, include the label and type.
  if (selectedCaseId && caseName && (selectedItemLabel !== undefined || selectedItemType !== undefined)) {
    const labelText =
      selectedItemLabel !== undefined
        ? t(
            /*
      t('scans.upper')
      t('scans.lower')
      t('scans.combined')
      t('scans.unspecified')
      */
            `scans.${selectedItemLabel}`
          )
        : "";

    // TODO: Replace modelType option "based" with "designedModel" & replace code below with commented line
    // const typeText = selectedItemType !== undefined ? t(`label.${selectedItemType}`) : "";

    let type = "";
    if (selectedItemType !== undefined) {
      type =
        selectedItemType === "based-model"
          ? t("label.designedModel")
          : t(
              /*
      t('label.scan')
      t('label.thumbnail')
      */
              `label.${selectedItemType}`
            );
    }
    const typeText = type;
    // Append with a separator, for example: "Case Name - Upper - scan"
    title = `${title}${labelText ? ` - ${labelText}` : ""}${typeText ? ` - ${typeText}` : ""}`;
  }

  return (
    <StyledTooltip title={title}>
      <Typography className={PH_NO_CAPTURE} fontSize="1.3em" fontWeight="500" noWrap sx={{ maxWidth: "100%" }}>
        {title}
      </Typography>
    </StyledTooltip>
  );
};

const Subtitle = () => {
  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];
  const selectedCaseId = useSelector((state: RootState) => selectDetailsItemId(state, basePath, selectedTab));
  const { status, eventLogs } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(selectedCaseId), {
    selectFromResult: ({ data }) => ({
      status: data?.status,
      eventLogs: data?.logs
    })
  });

  return status !== undefined ? <CaseStatusIndicator caseStatus={status} eventLogs={eventLogs ?? []} showExtendedErrors /> : null;
};

const ActionBarButtons = () => {
  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];
  const selectedCaseId = useSelector((state: RootState) => selectDetailsItemId(state, basePath, selectedTab));
  const { status } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(selectedCaseId), {
    selectFromResult: ({ data }) => ({
      status: data?.status
    })
  });

  if (selectedCaseId && status != undefined) {
    return (
      <Box
        sx={{
          display: "flex",
          gap: "0.5em",
          overflowY: "auto"
        }}
      >
        <RejectPillButton selectedCases={[selectedCaseId]} />
        <DownloadDesignsButton
          selectedCase={["done", "downloaded"].includes(status ?? "") ? [selectedCaseId] : undefined}
          dropdownPlacement="top"
        />
      </Box>
    );
  } else {
    return;
  }
};

const CaseInfo = ({ id }: { id: string }) => {
  const { t } = useTranslation("cases");
  const UNAVAILABLE = t("common:loading.unavailable");
  const { createdBy, createdAt, rejectedBy } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(id), {
    selectFromResult: ({ data }) => ({
      createdAt: data?.created_at,
      createdBy: data?.created_by,
      rejectedBy: data?.rejected_by
    })
  });

  const { data: uploadedByData } = useSearchUserApiV1UsersUserIdGetQuery(
    { userId: encodeURI(createdBy ?? "") },
    { skip: createdBy === undefined || createdBy === "" }
  );
  const { data: rejectedByData } = useSearchUserApiV1UsersUserIdGetQuery(
    { userId: encodeURI(rejectedBy ?? "") },
    { skip: rejectedBy == null || rejectedBy === "" }
  );
  const formattedDate = createdAt ? formatDateTime(createdAt) : UNAVAILABLE;

  const details: DetailsGridItem[] = [
    { name: t("label.uploadedAt"), value: formattedDate, valueAnalyticsVisible: true },
    {
      name: t("label.uploadedBy"),
      value: createdBy != null && uploadedByData?.name ? uploadedByData.name : UNAVAILABLE
    },
    ...(rejectedBy != null
      ? [
          {
            name: t("label.rejectedBy"),
            value: rejectedByData?.name ?? UNAVAILABLE
          }
        ]
      : [])
  ];

  return <DetailsGrid details={details} />;
};

const CaseOptions = ({ id }: { id: string }) => {
  const { t } = useTranslation("template");
  const UNAVAILABLE = t("common:loading.unavailable");
  const { options } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(id), {
    selectFromResult: ({ data }) => ({
      options: data?.case_options
    })
  });
  const no = t("common:dialog.no");
  const yes = t("common:dialog.yes");
  const boolTranslation = (cond: boolean) => (cond ? yes : no);

  const details: DetailsGridItem[] = [
    {
      name: t("label.modelType"),
      value:
        options?.base_params?.full_palate !== undefined
          ? options?.base_params?.full_palate
            ? t("options.modelType.fullPalate")
            : t("options.modelType.horseShoe")
          : UNAVAILABLE,
      valueAnalyticsVisible: true
    },
    {
      name: t("label.affixText"),
      value:
        options?.text_params?.text_style != null ? t(`options.textStyle.${options.text_params.text_style}`) : t("options.textStyle.none"),
      valueAnalyticsVisible: true
    },
    {
      name: t("label.hollowModel"),
      value: boolTranslation(options?.base_params?.hollow === true),
      valueAnalyticsVisible: true
    },
    {
      name: t("label.modelAttachmentTypes"),
      value:
        options?.attachment_params?.attachment_type !== undefined
          ? options?.attachment_params?.attachment_type === "pillars"
            ? t("options.modelAttachmentTypes.pillars")
            : options?.attachment_params?.attachment_type === "articulator"
              ? t("options.modelAttachmentTypes.hingedArticulator")
              : t("options.modelAttachmentTypes.none")
          : UNAVAILABLE,
      valueAnalyticsVisible: true
    }
  ];

  return <DetailsGrid details={details} />;
};

const CaseDetails = () => {
  const { t } = useTranslation("cases");

  const { id: selectedTab } = useParams();
  const location = useLocation();
  const basePath = location.pathname.split("/")[1];

  const drawerOpen = useSelector((state: RootState) => selectDrawerOpen(state));

  const selectedCaseId = useSelector((state: RootState) => selectDetailsItemId(state, basePath, selectedTab));

  const { error, isLoading } = useGetCaseApiV1CasesCaseIdGetQuery(getCaseQuery(selectedCaseId), {
    skip: selectedCaseId === undefined,
    pollingInterval: designTableConstants.POLLING_INTERVAL
  });

  const onClose = () => {
    store.dispatch(setDrawerOpen({ value: false }));
    store.dispatch(clearSelectedIds({ page: basePath, tab: selectedTab }));
  };

  if (!drawerOpen) {
    return <></>;
  }

  return (
    <Box sx={{ height: "100%" }}>
      {error ? (
        <ExtendedDetails
          loading={isLoading}
          close={onClose}
          title={<Title />}
          subtitle={<>{error && localizeError(t, ensureError(error))}</>}
          banner={
            <>
              <ViewportContainer sx={{ height: "100%" }}>
                <NoneSelectedSvg />
              </ViewportContainer>
            </>
          }
        ></ExtendedDetails>
      ) : (
        <ExtendedDetails
          loading={isLoading}
          title={<Title />}
          subtitle={<Subtitle />}
          close={onClose}
          banner={
            <>
              {selectedCaseId && <RetryBanner id={selectedCaseId} />}
              <CaseViewport actionBarButtons={<ActionBarButtons />} />
            </>
          }
          entries={
            selectedCaseId
              ? {
                  [t("label.caseInformation")]: {
                    content: <CaseInfo id={selectedCaseId} />,
                    startOpen: true
                  },
                  [t("label.template")]: {
                    content: <CaseOptions id={selectedCaseId} />,
                    startOpen: true
                  }
                }
              : undefined
          }
          footer={
            selectedCaseId && (
              <Box
                component="div"
                sx={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "flex-end",
                  overflowX: "auto",
                  overflowY: "hidden"
                }}
              >
                <ActionBarButtons />
              </Box>
            )
          }
        />
      )}
    </Box>
  );
};

export { CaseDetails };
